import React from "react";
import URI from "urijs";
import {
  getToastRoute,
  hasLocalSession,
  parseLangFromRelayParam,
  parseSearchParams,
  sendToFromIfExists,
} from ".";
import {
  PrescriptionDrug,
  CoverageInfo,
  BeneficiaryAndApiHeaders,
  Beneficiary,
  IdParts,
  Pharmacy,
  PharmacyType,
  OecStatus,
  UserLanguage,
  AppState,
  Action,
  ActionType,
  MctToastType,
  PDE,
  MctYearPartFlag,
} from "../@types";
import {
  loginBene,
  getCSRBeneInfo,
  getBeneOecStatus,
  getLoggedInBeneInfo,
  getBeneDrugs,
  getBenePharmacies,
  getCounty,
} from "../api";
import { ApiError, logError } from "./errors";
import { drugsAreDuplicates, uppercaseFirst } from "./prescriptionHelpers";
import { LDFlagSet } from "launchdarkly-js-sdk-common";
import { parseDegradedIntegrations } from "./loginHelpers";
import { fetchCurrentPlan, fetchPlanIdsForNextYear } from "./planHelpers";
import { RouteComponentProps } from "react-router";
import routes from "../app/routes";
import { getYearPartInfo } from "./yearPartHelpers";

// Intended to be used like so: call iniitateLogin() -> redirect to MBP & SLS for login ->
export async function getBeneficiaryInfo(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  // Ask the API to kindly fetch the details of our beneficiary
  const beneInfo = await loginBene(code);

  return beneInfo;
}

export async function getBeneInfoForCSR(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  return await getCSRBeneInfo(code);
}

export function getSLSCode(search: string): string {
  const { code } = parseSearchParams(URI.parseQuery(search));
  return code;
}

export const doesNotInclude = (
  acc: PrescriptionDrug[],
  { ndc, name }: { ndc: string; name: string }
): boolean => {
  return acc.every(
    ({ ndc: currentNDC, name: currentName }) =>
      currentNDC !== ndc && currentName !== name
  );
};

interface PlanIdParts extends IdParts {
  contract_year: string;
}

// todo: rename this to arePlansTheSame
export const isCurrentPlan = (
  coverageInfo: CoverageInfo | undefined,
  plan: PlanIdParts
): boolean => {
  if (!coverageInfo) return false;

  return (
    coverageInfo.plan_id === plan.plan_id &&
    coverageInfo.segment_id === plan.segment_id &&
    coverageInfo.contract_id === plan.contract_id &&
    (coverageInfo.contract_year === plan.contract_year ||
      coverageInfo.coverage_year === plan.contract_year)
  );
};

export const deduplicatePrescriptions = (
  prescriptions: PrescriptionDrug[]
): PrescriptionDrug[] => {
  return prescriptions.filter(
    (a, index) =>
      prescriptions.findIndex(b => drugsAreDuplicates(a, b)) === index
  );
};

export const getBeneFirstAndLastName = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  return firstName || beneficiary.lastname
    ? `${firstName} ${uppercaseFirst(beneficiary.lastname)}`.trim()
    : t("bene_landing.name_not_available");
};

export const getBeneFirstNameAndLastInitial = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  if (firstName && beneficiary.lastname) {
    return `${firstName} ${beneficiary.lastname[0].toUpperCase()}.`.trim();
  } else if (beneficiary.lastname) {
    return uppercaseFirst(beneficiary.lastname).trim();
  } else if (firstName) {
    return firstName;
  } else {
    return t("bene_landing.name_not_available");
  }
};

export const getPharmacyTypeFromSavedPharmacies = (
  pharmacies: Pharmacy[],
  mailOrder: boolean
): PharmacyType => {
  let pharmacyType = PharmacyType.NONE;
  const isRetail = pharmacies.length > 0;
  const isMail = mailOrder;

  if (isRetail && isMail) {
    pharmacyType = PharmacyType.MAIL_AND_RETAIL;
  } else if (isRetail) {
    pharmacyType = PharmacyType.RETAIL;
  } else if (isMail) {
    pharmacyType = PharmacyType.MAIL;
  }

  return pharmacyType;
};

export const fetchOECStatus = async (
  year: string
): Promise<OecStatus | undefined> => {
  try {
    const status = await getBeneOecStatus(year);
    return status;
  } catch (_e) {
    const e = _e as ApiError;
    logError("Failed to get beneficiary OEC status (GlobalSessionHandler)", e);
    return undefined;
  }
};

export const fetchAndSetOECStatusForYearPart = async ({
  mctYearPart,
  currentYear,
  dispatch,
}: {
  mctYearPart: MctYearPartFlag;
  currentYear: string;
  dispatch: React.Dispatch<Action>;
}): Promise<void> => {
  let oecStatusCurrentYear;
  let oecStatusNextYear;
  const { isOpenEnrollmentNextYearOnly, isOpenEnrollment } = getYearPartInfo({
    mctYearPart,
  });
  if (!isOpenEnrollmentNextYearOnly) {
    oecStatusCurrentYear = await fetchOECStatus(`${currentYear}`);
  }
  if (isOpenEnrollmentNextYearOnly || isOpenEnrollment) {
    oecStatusNextYear = await fetchOECStatus(`${+currentYear + 1}`);
  }
  dispatch({
    type: ActionType.UPDATE_OEC_STATUSES,
    payload: { currentYear: oecStatusCurrentYear, nextYear: oecStatusNextYear },
  });
};

/**
 * Set or update beneficiary, LIS, and future LIS in app state (store)
 */
export const updateBeneInfo = ({
  beneInfo,
  dispatch,
}: {
  beneInfo?: Beneficiary;
  dispatch: React.Dispatch<Action>;
}) => {
  const fips = beneInfo?.mailing_address.fips_state_county;

  dispatch({
    type: ActionType.ADD_BENEFICIARY,
    payload: beneInfo,
  });

  dispatch({
    type: ActionType.UPDATE_LIS,
    payload: beneInfo?.lis_level,
  });

  dispatch({
    type: ActionType.UPDATE_FUTURE_LIS,
    payload: beneInfo?.future_lis_level,
  });

  // By default, use bene's mailing address to set root-level `fips` and `zipcode`
  // in `AppState`. Keeps LI bene from being prompted for these in various user-flows
  if (beneInfo?.mailing_address.zipcode) {
    dispatch({
      type: ActionType.UPDATE_ZIPCODE,
      payload: beneInfo.mailing_address.zipcode,
    });

    if (fips) {
      getCounty(fips).then(county => {
        dispatch({
          type: ActionType.UPDATE_FIPS,
          payload: fips,
        });

        dispatch({ type: ActionType.UPDATE_COUNTY, payload: county });
      });
    }
  }
};

export interface GetBeneficiaryParams {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  location: RouteComponentProps["location"];
  history: RouteComponentProps["history"];
  flags: LDFlagSet;
  beneInfoWithHeaders?: BeneficiaryAndApiHeaders;
}

/**
 * Gets bene info if there's a session but no bene in the store
 * or if coming from a callback route via MBP
 */
export const getBeneficiary = async ({
  state,
  dispatch,
  location,
  history,
  flags,
  beneInfoWithHeaders,
}: GetBeneficiaryParams): Promise<void> => {
  // These query params are provided on the sls-callback route
  // `code` is useed to initiate login
  // `relay`, if available, is used to update user language
  const { req_token: code, relay } = parseSearchParams(
    URI.parseQuery(location.search)
  );
  const userLang = parseLangFromRelayParam(relay) || UserLanguage.ENGLISH;
  const handlerName = "GlobalSessionHandler";
  // @TODO - `code` and `relay` are login-callback specific query params (I think)
  // Do we need this other (simpler) way of identifying login callback?
  const isLoginCallbackRoute = location.pathname === routes.slsCallback;
  const isLandingPage = location.pathname === "/";
  const isLoggedIn = hasLocalSession();
  // If there's a "sid" cookie but no bene info in app state (the store), the store
  // will need to be updated
  const needsUserRefresh = isLoggedIn && !state.beneficiary;
  const hadMbpHandoffFailure = state.mbpHandoffFailed;
  const {
    isOutsideOpenEnrollment,
    isOpenEnrollmentNextYearOnly,
  } = getYearPartInfo(flags);

  const authLandingRoute = routes.summary.landingPage;

  // Carry over from pre-global-session handling. `code` is a query param on login
  // callback, and refresh indicates we have a local session from MCTAPI, but no bene
  // in app state
  if (code || needsUserRefresh) {
    // Clear out any previous bene info, if not already cleared via sessionStorage
    // @TODO - Revisit when this dispatch should happen. With the GlobalSessionHandler,
    // there are more scenarios when state will need to be reset than just on
    // the sls-callback route
    code &&
      dispatch({
        type: ActionType.RESET_STATE,
      });

    // Persist MBP handoff failure, even if the rest of state is reset
    if (hadMbpHandoffFailure) {
      dispatch({
        type: ActionType.UPDATE_MBP_HANDOFF_FAILED,
        payload: true,
      });
    }

    try {
      const { beneficiary: beneInfo, headers } = code
        ? await loginBene(code)
        : beneInfoWithHeaders || (await getLoggedInBeneInfo());

      const degradedIntegrations = parseDegradedIntegrations(headers);

      if (degradedIntegrations.length) {
        dispatch({
          type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
          payload: degradedIntegrations,
        });
      }

      updateBeneInfo({
        beneInfo,
        dispatch,
      });

      try {
        const drugs = await getBeneDrugs();
        dispatch({
          type: ActionType.SET_PRESCRIPTIONS,
          payload: drugs,
        });
      } catch (e) {
        logError(
          `Failed to get beneficiary drugs (${handlerName})`,
          e as ApiError
        );
      }

      try {
        const { pharmacies, mail_order } = await getBenePharmacies();
        dispatch({
          type: ActionType.SET_PHARMACIES,
          payload: pharmacies,
        });

        dispatch({
          type: ActionType.UPDATE_PHARMACY_TYPE,
          payload: getPharmacyTypeFromSavedPharmacies(pharmacies, mail_order),
        });
      } catch (e) {
        logError(
          `Failed to get beneficiary pharmacies (${handlerName})`,
          e as ApiError
        );
      }

      if (beneInfo?.current_coverage.length) {
        let nextYearPlanIds;
        if (!isOutsideOpenEnrollment || isOpenEnrollmentNextYearOnly) {
          nextYearPlanIds = await fetchPlanIdsForNextYear(
            beneInfo.current_coverage[0],
            handlerName
          );
        }
        await fetchCurrentPlan(
          beneInfo,
          state.pharmacies,
          beneInfo.lis_level,
          dispatch,
          nextYearPlanIds,
          beneInfo.future_lis_level
        );
      }

      // @TODO - figure out which other logic, if any, needs to run only on login callback route
      // or on any route other than login callback
      // `isLoginCallbackRoute` added here, since other routes should return to landing
      if (needsUserRefresh) {
        if (isLoginCallbackRoute) {
          console.debug(
            `Returning from getBeneficiary on login callback, needsUserRefresh, bene has been updated in state`
          );
          return sendToFromIfExists(history);
        } else if (isLandingPage) {
          console.debug(
            `Returning from getBeneficiary on navigation to main landing page, needsUserRefresh, bene has been updated in state`
          );
          return history.replace(`/?lang=${userLang}`);
        } else {
          console.debug(
            `Returning from getBeneficiary, bene has been updated in state, routing to bene landing`
          );
          return history.replace(`${authLandingRoute}?lang=${userLang}`);
        }
      } else {
        console.debug(
          `Returning from getBeneficiary, bene has been updated in state, routing to bene landing`
        );
        return history.replace(`${authLandingRoute}?lang=${userLang}`);
      }
    } catch (e) {
      logError(
        `Failed to retrieve beneficiary (${handlerName})`,
        e as ApiError
      );

      // @TODO - If we've gotten here, there was a "sid" but no state.beneficiary, so why
      // are we trying to send the user to `history.location.state.from`?
      // Also, does this only make sense on login callback?
      if (needsUserRefresh) {
        return sendToFromIfExists(history);
      } else {
        // @TODO - Should this re-throw to allow the GlobalSessionHandler to actively
        // logout?
        return history.replace(
          getToastRoute(
            `${routes.questionRouting}?lang=${userLang}`,
            isLoginCallbackRoute
              ? MctToastType.BENE_FAILED_LOGIN
              : MctToastType.SESSION_EXPIRED
          )
        );
      }
    }
  } else if (state.beneficiary) {
    console.debug(`Returning from getBeneficiary, bene already in app state`);
    return history.replace(`${authLandingRoute}?lang=${userLang}`);
  } else {
    console.debug(`Returning from getBeneficiary, bene failed login`);
    // @TODO - Should this re-throw to allow the GlobalSessionHandler to actively
    // logout?
    return history.replace(
      getToastRoute(
        `${routes.questionRouting}?lang=${userLang}`,
        isLoginCallbackRoute
          ? MctToastType.BENE_FAILED_LOGIN
          : MctToastType.SESSION_EXPIRED
      )
    );
  }
};

/**
 * Calculates from midnight-to-midnight
 */
export const isOverThirtyDaysSinceLogin = (
  beneficiary: Beneficiary
): boolean => {
  const {
    meta_data: { last_login_date },
  } = beneficiary;
  if (null === last_login_date || !last_login_date) {
    return true;
  }
  const today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  const lastLoginDate = new Date(last_login_date).getTime();
  const daysSinceLastLogin = (today - lastLoginDate) / (1000 * 60 * 60 * 24);
  return daysSinceLastLogin > 30;
};

export const getPDEInfo = (
  prescriptions: PrescriptionDrug[],
  pdes?: PDE[]
): { savedPdes: PrescriptionDrug[]; unsavedPdes: PrescriptionDrug[] } => {
  const savedPdes: PrescriptionDrug[] = [];
  const unsavedPdes: PrescriptionDrug[] = [];

  if (pdes) {
    pdes.forEach(p => {
      if (!prescriptions.find(d => d.ndc === p.drug.ndc)) {
        unsavedPdes.push(p.drug);
      } else {
        savedPdes.push(p.drug);
      }
    });
  }

  return { savedPdes, unsavedPdes };
};
